/**
 * Onglet "Leads" du back-office B2B.
 *
 * Affiche toutes les demandes /prestations-pro et permet de les convertir
 * en fiches client B2B (carnet d'adresses) en 1 clic.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../../components/ui/dialog';
import { UserPlus, Mail, Phone, MapPin, ExternalLink, CheckCircle2, CalendarPlus } from 'lucide-react';
import api from '../../../api';
import { toast } from 'sonner';

const fmtInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const toISO = (s) => (s ? new Date(s).toISOString() : null);

export const B2BLeadsTab = ({ onImported, onGoToClient }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(null);
  const [meta, setMeta] = useState({ prestation_types: [] });
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleLead, setScheduleLead] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({ scheduled_start: '', scheduled_end: '', prestation_type: '', admin_notes: '' });
  const [savingSchedule, setSavingSchedule] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, m] = await Promise.all([
        api.b2bListLeads(200),
        api.b2bAgendaMeta().catch(() => ({ prestation_types: [] })),
      ]);
      setLeads(r.items || []);
      setMeta(m);
    } catch (e) {
      toast.error('Erreur chargement leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleImport = async (lead) => {
    setImporting(lead.id);
    try {
      const r = await api.b2bImportLead(lead.id);
      toast.success(r.created
        ? `Client "${r.client.name}" créé dans le CRM`
        : `Lead lié au client existant "${r.client.name}"`);
      await load();
      if (onImported) onImported();
    } catch (e) {
      toast.error(e.message || "Échec de l'import");
    } finally {
      setImporting(null);
    }
  };

  const openSchedule = (lead) => {
    setScheduleLead(lead);
    setScheduleForm({
      scheduled_start: fmtInput(lead.scheduled_start),
      scheduled_end: fmtInput(lead.scheduled_end),
      prestation_type: lead.prestation_type || '',
      admin_notes: lead.admin_notes || '',
    });
    setScheduleOpen(true);
  };

  const handleSchedule = async () => {
    if (!scheduleForm.scheduled_start) {
      toast.error('Date de début obligatoire');
      return;
    }
    setSavingSchedule(true);
    try {
      const payload = {
        scheduled_start: toISO(scheduleForm.scheduled_start),
        scheduled_end: toISO(scheduleForm.scheduled_end) || toISO(scheduleForm.scheduled_start),
        prestation_type: scheduleForm.prestation_type || null,
        admin_notes: scheduleForm.admin_notes || null,
      };
      await api.b2bScheduleLead(scheduleLead.id, payload);
      toast.success('Lead planifié dans l\'agenda');
      setScheduleOpen(false);
      await load();
      if (onImported) onImported();
    } catch (e) {
      toast.error(e.message || 'Erreur planification');
    } finally {
      setSavingSchedule(false);
    }
  };

  if (loading && leads.length === 0) {
    return <div className="p-8 text-center text-sm text-muted-foreground">Chargement…</div>;
  }

  if (leads.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <div className="text-muted-foreground">Aucune demande de devis reçue pour le moment.</div>
          <div className="text-xs text-muted-foreground mt-2">
            Les demandes envoyées via <code>/prestations-pro</code> apparaissent ici.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="b2b-leads-list">
      <div className="text-xs text-muted-foreground">
        {leads.length} demande{leads.length > 1 ? 's' : ''} reçue{leads.length > 1 ? 's' : ''} via le formulaire B2B.
      </div>
      {leads.map((l) => {
        const isImported = l.status === 'imported';
        const created = l.created_at ? new Date(l.created_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—';
        return (
          <Card key={l.id} data-testid={`b2b-lead-${l.id}`}>
            <CardContent className="p-5">
              <div className="flex flex-col md:flex-row md:items-start gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-black truncate">{l.school_name || '—'}</h3>
                    {l.structure_type && (
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{l.structure_type}</span>
                    )}
                    {isImported ? (
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 inline-flex items-center gap-1">
                        <CheckCircle2 size={11} /> Importé
                      </span>
                    ) : (
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600">À traiter</span>
                    )}
                    {l.scheduled_start && (
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-600 inline-flex items-center gap-1">
                        <CalendarPlus size={11} /> Planifié {new Date(l.scheduled_start).toLocaleDateString('fr-FR')}
                      </span>
                    )}
                  </div>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                    {l.contact_name && <div><span className="text-muted-foreground">Contact :</span> <strong>{l.contact_name}</strong></div>}
                    {l.city && <div className="flex items-center gap-1"><MapPin size={12} className="text-muted-foreground" />{l.city}</div>}
                    {l.contact_email && (
                      <div className="flex items-center gap-1 min-w-0">
                        <Mail size={12} className="text-muted-foreground flex-shrink-0" />
                        <a href={`mailto:${l.contact_email}`} className="text-[hsl(var(--primary))] hover:underline truncate">{l.contact_email}</a>
                      </div>
                    )}
                    {l.contact_phone && (
                      <div className="flex items-center gap-1">
                        <Phone size={12} className="text-muted-foreground" />
                        <a href={`tel:${l.contact_phone}`} className="text-[hsl(var(--primary))] hover:underline">{l.contact_phone}</a>
                      </div>
                    )}
                  </div>
                  {l.needs && (
                    <div className="mt-3 p-3 rounded-lg bg-muted/40 border border-border text-xs leading-relaxed">
                      <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">Besoin exprimé</div>
                      <div className="whitespace-pre-wrap">{l.needs}</div>
                      {l.dates && <div className="mt-2 text-muted-foreground">📅 Dates : <strong>{l.dates}</strong></div>}
                    </div>
                  )}
                  <div className="mt-2 text-[10px] text-muted-foreground">Reçu le {created}</div>
                </div>
                <div className="flex md:flex-col gap-2 flex-shrink-0">
                  {!isImported ? (
                    <Button
                      size="sm"
                      disabled={importing === l.id}
                      onClick={() => handleImport(l)}
                      data-testid={`b2b-import-lead-${l.id}`}
                      className="whitespace-nowrap"
                    >
                      <UserPlus size={14} className="mr-1.5" />
                      {importing === l.id ? 'Import…' : 'Créer fiche client'}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { if (onGoToClient) onGoToClient(); }}
                      data-testid={`b2b-view-client-${l.id}`}
                      className="whitespace-nowrap"
                    >
                      <ExternalLink size={14} className="mr-1.5" />Voir client
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant={l.scheduled_start ? 'default' : 'outline'}
                    onClick={() => openSchedule(l)}
                    data-testid={`b2b-schedule-lead-${l.id}`}
                    className="whitespace-nowrap"
                  >
                    <CalendarPlus size={14} className="mr-1.5" />
                    {l.scheduled_start ? 'Replanifier' : 'Planifier'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* DIALOG PLANIFICATION */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent className="max-w-lg" data-testid="b2b-lead-schedule-dialog">
          <DialogHeader>
            <DialogTitle>Planifier dans l'agenda</DialogTitle>
            <DialogDescription>
              {scheduleLead?.school_name} — saisie des dates précises pour apparition sur l'agenda
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {scheduleLead?.dates && (
              <div className="text-xs p-2 rounded bg-muted/40 border border-border">
                <div className="font-bold mb-0.5">Demande client :</div>
                <div>{scheduleLead.dates}</div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Début *</Label>
                <Input
                  type="datetime-local"
                  value={scheduleForm.scheduled_start}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, scheduled_start: e.target.value })}
                  data-testid="b2b-lead-schedule-start"
                />
              </div>
              <div>
                <Label>Fin (optionnel)</Label>
                <Input
                  type="datetime-local"
                  value={scheduleForm.scheduled_end}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, scheduled_end: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Type de prestation</Label>
              <Select value={scheduleForm.prestation_type || 'none'} onValueChange={(v) => setScheduleForm({ ...scheduleForm, prestation_type: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="b2b-lead-schedule-type"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Non précisé —</SelectItem>
                  {meta.prestation_types.map((pt) => (
                    <SelectItem key={pt.value} value={pt.value}>
                      <span className="inline-flex items-center gap-2">
                        <span style={{ backgroundColor: pt.color }} className="w-2 h-2 rounded-full inline-block" />
                        {pt.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes admin (optionnel)</Label>
              <Textarea
                rows={2}
                value={scheduleForm.admin_notes}
                onChange={(e) => setScheduleForm({ ...scheduleForm, admin_notes: e.target.value })}
                placeholder="Notes internes sur cette intervention…"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleOpen(false)} disabled={savingSchedule}>Annuler</Button>
            <Button onClick={handleSchedule} disabled={savingSchedule} data-testid="b2b-lead-schedule-save">
              {savingSchedule ? 'Enregistrement…' : 'Planifier'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
